import { FeatureFlags, FormatHelper, MDFCore } from '@adp-wfn/mdf-core';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import { GridPreDestroyedEvent, GridReadyEvent, ModelUpdatedEvent, ModuleRegistry, RowDataUpdatedEvent } from 'ag-grid-community';
import { AllEnterpriseModule, ColDef, ColGroupDef, ICellRendererParams, LicenseManager } from 'ag-grid-enterprise';
import { omit } from 'lodash';
import { SdfAlert, SdfAlertBanner } from '@waypoint/react-components';

// Notes
// AgGrid Documentation Link: https://www.ag-grid.com/react-grid/grid-interface/
// AgGrid is being served out of mdf as is without having any modifications to the interface and its properties.
// Please refer to the link for more information on AgGrid properties, interface, callbacks and api methods.
// Redux Note: It is mandatory for application teams to have an unique id property on each row data object in
// order for the react-redux to work and reflect data modifications to the grid.

export type IAgGridProps = Record<string, any>;

// set license to access enterprise version of ag-grid
/*
fetch('https://synerg.adp.com/data/keys.json')
  .then((response) => response.json())
  .then((keys) => LicenseManager.setLicenseKey(keys.grid))
  .catch((error) => console.error('Error fetching keys.json:', error));
*/

const agGridKey = 'Using_this_{AG_Grid}_Enterprise_key_{AG-069316}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{ADP_LLC}_is_granted_a_{Multiple_Applications}_Developer_License_for_{20}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{35}_Production_Environments___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{8_February_2026}____[v3]_[01]_MTc3MDUwODgwMDAwMA==f4d5c24f6104132581a6ce339b7a2d54';
LicenseManager.setLicenseKey(agGridKey);
ModuleRegistry.registerModules([AllEnterpriseModule]);

const getLocaleText = () => {
  return {
    decimalSeparator: FormatHelper.getLocaleFormats().numberFormat.decimalSeparator,
    first: FormatHelper.formatMessage('@@First'),
    firstPage: `${FormatHelper.formatMessage('@@First')} ${FormatHelper.formatMessage('@@label_page')}`,
    last: FormatHelper.formatMessage('@@Last'),
    lastPage: `${FormatHelper.formatMessage('@@Last')} ${FormatHelper.formatMessage('@@label_page')}`,
    more: FormatHelper.formatMessage('@@More'),
    nextPage: `${FormatHelper.formatMessage('@@Next')} ${FormatHelper.formatMessage('@@label_page')}`,
    page: FormatHelper.formatMessage('@@label_page'),
    previousPage: `${FormatHelper.formatMessage('@@Previous')} ${FormatHelper.formatMessage('@@label_page')}`,
    of: FormatHelper.formatMessage('@@of'),
    thousandSeparator: FormatHelper.getLocaleFormats().numberFormat.thousandSeparator,
    to: FormatHelper.formatMessage('@@conn_to_lower'),
    totalAndFilteredRows: FormatHelper.formatMessage('@@label_rows'),
    totalRows: FormatHelper.formatMessage('@@TotalRows'),
    pageSizeSelectorLabel: FormatHelper.formatMessage('@@PAGE_SIZE'),
    pinColumn: FormatHelper.formatMessage('@@PIN_COLUMN'),
    pinLeft: FormatHelper.formatMessage('@@PIN_LEFT'),
    pinRight: FormatHelper.formatMessage('@@PIN_RIGHT'),
    noPin: FormatHelper.formatMessage('@@NO_PIN'),
    autosizeThisColumn: FormatHelper.formatMessage('@@AUTOSIZE_THIS_COLUMN'),
    autosizeAllColumns: FormatHelper.formatMessage('@@AUTOSIZE_ALL_COLUMNS'),
    chooseColumns: FormatHelper.formatMessage('@@CHOOSE_COLUMNS'),
    columnChooser: FormatHelper.formatMessage('@@CHOOSE_COLUMNS'),
    resetColumns: FormatHelper.formatMessage('@@RESET_COLUMNS'),
    sortAscending: FormatHelper.formatMessage('@@SORT_ASCENDING'),
    sortDescending: FormatHelper.formatMessage('@@SORT_DESCENDING'),
    sortUnSort: FormatHelper.formatMessage('@@CLEAR_SORT')
  };
};

const checkComponents = (components: object) => {
  let allGood = true;

  for (const key in components) {
    if (Object.hasOwn(components, key)) {
      if (!components[key]) {
        console.error(`AgGrid.checkComponents(): AgGrid props.components[${key}] does not exist!`, components[key]);
        allGood = false;
      }
      else if (!(components[key].prototype?.getGui && components[key].isValid)) {
        console.error(`AgGrid.checkComponents(): AgGrid props.components[${key}] is either not a valid renderer/editor or was not registered with the AgGridManager!`, components[key]);
        allGood = false;
      }
    }
  }

  return allGood;
};

// AgGrid react wrapper
export const AgGrid = (props: IAgGridProps) => {
  const [loadingText, setLoadingText] = useState(' ');
  const [noRowContent, setNoRowContent] = useState<any>('');
  const [noRowToggleContent, setNoRowToggleContent] = useState<any>('');

  if (MDFCore.shouldLog()) {
    console.log('AgGrid(): Entering. props =', props);
  }

  let allComponentsValid = true;

  if (FeatureFlags.hasFeature('agGridIsStrict') && props.components) {
    allComponentsValid = checkComponents(props.components);
  }

  const gridRef = useRef();
  const { borders, gridHeight, headerHeight, id, messageText, rowHeight } = props;
  let { width } = props;
  const filteredProps = omit(props, ['gridHeight', 'headerHeight', 'id', 'messageText', 'rowHeight', 'rowInsert', 'style', 'width', 'onGridReady', 'onGridPreDestroyed']);
  const isVDL = window['isADPUnified'] === undefined;
  let themeName = 'ag-theme-adp-33 ag-wfn-focused';
  // The default row heights:
  //   VDL - 40px
  //   OneUX - 48px (3rem, but we have to use pixels here)
  const defaultRowHeight = isVDL ? 40 : 48;
  const defaultHeaderRowHeight = isVDL ? 40 : 48;

  // Turn off row striping by default
  themeName = props.striped ? themeName : `${themeName} no-stripes`;
  // Allow the use of borders
  themeName = borders ? `${themeName} borders-${borders}` : themeName;

  // To disable minimum height of 150px of grid.
  if (props.disableMinHeight) {
    themeName = `${themeName} ag-disable-min-height`;
  }

  const height = gridHeight || '100%';
  width = width || '100%';

  if (typeof height !== 'string') {
    console.error('AgGrid(): gridHeight must be a string as it is a style property. The value must also include the units.');
  }

  if (typeof width !== 'string') {
    console.error('AgGrid(): width must be a string as it is a style property. The value must also include the units.');
  }

  filteredProps.headerHeight = headerHeight || defaultHeaderRowHeight;
  filteredProps.rowHeight = rowHeight || defaultRowHeight;

  // Default for floating-filter header height
  filteredProps.floatingFiltersHeight = filteredProps.headerHeight;

  // Add a default no rows message
  if (!props.overlayNoRowsTemplate) {
    filteredProps.overlayNoRowsTemplate = `<div class="p-8 text-base">${FormatHelper.formatMessage('@@NoRowsFound')}</div>`;
  }

  const context = useMemo(() => ({
    dispatch: useDispatch(),
    store: useStore()
  }), []);

  const localeText = useMemo(() => getLocaleText(), []);

  // Add aria-label for Ag-grid after its ready
  const onGridReady = useCallback((params: GridReadyEvent) => {
    if (props['aria-label']) {
      const node = gridRef.current as any;
      node.api.setGridAriaProperty('label', props['aria-label']);
    }

    // Call the onGridReady event defined in the view if it's available
    props.onGridReady?.(params);
  }, []);

  const onModelUpdated = useCallback((params: ModelUpdatedEvent) => {
    const newLoadingText = params.keepRenderedRows ? FormatHelper.formatMessage('@@AG_GRID_LOADING_COMPLETED') : FormatHelper.formatMessage('@@AG_GRID_LOADING');

    if (MDFCore.shouldLog()) {
      console.log('onModelUpdated(): Entering. params = ', params);
    }

    if (loadingText !== newLoadingText && props.rowModelType === 'serverSide') {
      setTimeout(() => {
        setLoadingText(newLoadingText);
      }, 1);
    }
  }, []);

  const onRowDataUpdated = useCallback((params: RowDataUpdatedEvent) => {
    // Check if there are no rows rendered
    const noRowMessage = FormatHelper.formatMessage('@@NoRowsFound');
    const newNoRowContent = params.api.getRenderedNodes().length === 0 ? noRowMessage : '';

    // clear noRowContent when noRowContent not empty and noRowContent, newNoRowContent are same
    // use noRowToggleContent to force screen reader re-announcement
    if (newNoRowContent && noRowContent && noRowContent === newNoRowContent) {
      setTimeout(() => {
        setNoRowContent('');
        setNoRowToggleContent(newNoRowContent);

        // Log the change for debugging purposes
        if (MDFCore.shouldLog()) {
          console.log('onRowDataUpdated(): Updated noRowContent:', newNoRowContent);
        }
      }, 1);
    }
    else {
      setTimeout(() => {
        setNoRowContent(newNoRowContent);
        setNoRowToggleContent('');

        // Log the change for debugging purposes
        if (MDFCore.shouldLog()) {
          console.log('onRowDataUpdated(): Updated noRowContent:', newNoRowContent);
        }
      }, 1);
    }

    // Call the onRowDataUpdated event defined in the view if it's available
    props.onRowDataUpdated?.(params);
  }, [noRowContent]);

  const onGridPreDestroyed = useCallback((params: GridPreDestroyedEvent) => {
    if (MDFCore.shouldLog()) {
      console.log('onGridPreDestroyed(): Entering. params = ', params);
    }

    props.onGridPreDestroyed?.(params);
  }, []);

  const onKeyDown = useCallback((event: any) => {
    const key = (event.event as KeyboardEvent).key;
    const gridApi = event.api;

    if (MDFCore.shouldLog()) {
      console.log('AgGrid.onKeyDown(): event =', event);
      console.log('AgGrid.onKeyDown(): key =', key);
    }

    if (!gridApi) {
      return;
    }

    switch (key) {
      case 'F7':
        if (event.rowIndex === 0 || (event.rowIndex % gridApi.paginationGetPageSize() === 0)) {
          return;
        }

        gridApi.setFocusedCell(event.rowIndex - 1, event.column, event.rowPinned);
        break;

      case 'F8':
        if (event.rowIndex + 1 >= gridApi.getDisplayedRowCount() || ((event.rowIndex + 1) % gridApi.paginationGetPageSize() === 0)) {
          return;
        }

        gridApi.setFocusedCell(event.rowIndex + 1, event.column, event.rowPinned);
        break;

      case 'F9':
        props.rowInsert?.(event);
        break;

      default:
        return;
    }
  }, []);

  const setCellRendererSelector = (columnDef: ColDef) => {
    // Check for user provided cellRendererSelector and store it.
    const userProvidedSelector = columnDef['cellRendererSelector'];

    columnDef['cellRendererSelector'] = (params: ICellRendererParams) => {
      // Add an aria description to the editable cell.
      params.eParentOfValue.setAttribute('aria-description', FormatHelper.formatMessage('@@EDITABLE_TEXT_FIELD'));
      return userProvidedSelector?.(params);
    };
  };

  const findChildrenDef = (columnDef: ColDef | ColGroupDef) => {
    if ((columnDef as ColDef).editable) {
      setCellRendererSelector(columnDef);
    }

    if (!(columnDef as ColGroupDef).children) {
      return;
    }

    // Recursion to find all children columns
    (columnDef as ColGroupDef).children.forEach((childDef) => {
      findChildrenDef(childDef);
    });
  };

  filteredProps.columnDefs?.forEach((columnDef: ColDef | ColGroupDef) => {
    findChildrenDef(columnDef);
  });

  if (props.domLayout === 'autoHeight' && (props.rowModelType === 'serverSide' || props.rowModelType === 'infinite') && !props.pagination) {
    console.error('This AgGrid will fail soon as "autoHeight" domLayout and infinite scrolling cannot be mixed.');
  }

  filteredProps.localeText = localeText;

  return (
    <React.Fragment>
      {!allComponentsValid && <SdfAlertBanner className="mb-2" status="error">This AgGrid has invalid renderers and/or editors. Please fix.</SdfAlertBanner>}
      {messageText && <SdfAlert className="mb-2" type="inline" hideIcon={true} ariaLive="polite" size="md" status="neutral">{messageText}</SdfAlert>}
      <div className="mdf-sr-only" aria-live="polite" aria-atomic={true} >{loadingText}</div>
      <div className="mdf-sr-only" aria-live="polite" aria-atomic={true} >{noRowContent}</div>
      <div className="mdf-sr-only" aria-live="polite" aria-atomic={true} >{noRowToggleContent}</div>
      <div className={themeName} id={id} style={{ height, width }}>
        <AgGridReact ref={gridRef} {...filteredProps} context={context} onGridReady={onGridReady} onCellKeyDown={onKeyDown} onGridPreDestroyed={onGridPreDestroyed} onModelUpdated={onModelUpdated} onRowDataUpdated={onRowDataUpdated} />
      </div>
    </React.Fragment>
  );
};
